/*
 *   File : mandates.js
 *   Author URI : https://evoqins.com
 *   Description : Modal screen to show available mandates created by selected bank account
 *   Integrations : null
 *   Version : v1.1
 */

import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { parse, differenceInYears, format, subYears } from 'date-fns';

import Icon from "../Icon/icon";
import { CustomSelectBox, CustomTextInput, DatePicker, OTPInput, RadioGroup } from "../Form-elements";
import { CustomTab } from "../Tab";
import { GradientButton, OutlineButton } from "../CTA";
import { CustomLoader } from "../Other";
import APIService from "../../Service/api-service";
import { _consent2FA, _getProfile } from "../../Helper/api";

import style from "../../Styles/Component/mange-nominee.module.scss";
import Colors from "../../Styles/color.module.scss";
import { useSelector } from "react-redux";
import { _convertToLabelIdArray } from "../../Helper";

const OptOptions = [{
    id: 1,
    label: "Opt-in"
},
{
    id: 2,
    label: "Opt-out"
}
]

const SOA_DECLARATIONS = [{
    id: 1,
    label: "Show all nominee names"
},
{
    id: 2,
    label: "Show nominee status"
}]

const NOMINEE = {
    nominee_id: null,
    name: "",
    relation: null,
    allocation: "",
    date_of_birth: null,
    guardian_name: "",
    guardian_relationship: null,
    guardian_pan: "",
    ageCheck: true,
    guardian_pan: "",
    proof: null,
    proof_number: "",
    nominee_email: "",
    nominee_mobile: "",
    nominee_address_line1: "",
    nominee_address_line2: "",
    nominee_address_line3: "",
    country: { label: "India", value: 101 },
    state: null,
    city: null,
    pincode: "",
    email_declaration: null,
    mobile_declaration: null,
    soa_declaration: SOA_DECLARATIONS[0].id
};

const NOMINEE_ERROR = {
    name: "",
    date_of_birth: "",
    relation: "",
    allocation: "",
    guardian_name: "",
    guardian_relationship: "",
    guardian_pan: "",
    proof: "",
    proof_number: "",
    nominee_email: "",
    nominee_mobile: "",
    nominee_address_line1: "",
    nominee_address_line2: "",
    nominee_address_line3: "",
    country: "",
    state: "",
    city: "",
    pincode: "",
    email_declaration: "",
    mobile_declaration: "",
}

const ManageNomineeModal = (props) => {

    const navigator = useNavigate();
    const location = useLocation();

    const GENERAL_DATA = useSelector(state => state.generalData.GENERAL_DATA)
    const nomineeErrorRef = useRef([NOMINEE_ERROR]);
    const [selectedOptOptions, setSelectedOptOptions] = useState(OptOptions[0].id);
    const [nomineeDetail, setNomineeDetail] = useState([]);
    const [forceUpdate, setForceUpdate] = useState(false);
    const [tabData, setTabData] = useState([]);
    const [tabIndex, setTabIndex] = useState(1);
    const [deleteHover, setDeleteHover] = useState(false);
    const [loader, setLoader] = useState(true);
    const [tabLoader, setTabLoader] = useState(true);
    const [relationOptions, setRelationOptions] = useState([]);
    const [guardianOptions, setGuardianOptions] = useState([]);
    const [proceed, setProceed] = useState(false);
    const [pinString, setPinString] = useState("");
    const [pinError, setPinError] = useState("");
    const [apiLoader, setApiLoader] = useState(false);
    const [seconds, setSeconds] = useState(30);
    const [showSuccess, setShowSuccess] = useState(false);

    const [countryOption, setCountryOptions] = useState([]);
    const [identityProofOptions, setIdentityProofOptions] = useState([])
    const [declarationOptions, setDeclarationOptions] = useState([]);
    const [isMinor, setIsMinor] = useState(false);

    // max date that can be selected for dob (current date - 18 years)
    const maxDate = new Date();
    // maxDate.setFullYear(maxDate.getFullYear() - 18);

    useEffect(() => {
        _getCountries();
    }, []);

    useEffect(() => {
        const proofs = GENERAL_DATA.nominee_id_proof.map((item) => ({
            label: item.name,
            value: item.id
        }))
        setIdentityProofOptions(proofs)
        const signup_relation = GENERAL_DATA.signup_relation.map((item) => ({
            label: item.name,
            value: item.id
        }))
        setDeclarationOptions(signup_relation)
    }, [GENERAL_DATA])

    useEffect(() => {
        var my_modal = document.getElementById("manage-nominee");
        my_modal.addEventListener("hidden.bs.modal", () => {
            props.close();
        });
    }, [props.close]);

    useEffect(() => {
        if (proceed === true) {
            const countdown = setInterval(() => {
                if (seconds > 0) {
                    setSeconds(seconds - 1);
                } else {
                    clearInterval(countdown);
                    // Handle expiration logic here (e.g., show a resend button)
                }
            }, 1000);

            return () => {
                clearInterval(countdown);
            };
        } else {
            setSeconds(30);
        }
    }, [seconds, proceed]);

    useEffect(() => {
        const _generateTabData = (length) =>
            Array.from({ length }, (_, index) => `Nominee ${index + 1}`);

        const tab_data = _generateTabData(nomineeDetail.length === 0 ? 1 : nomineeDetail.length);
        setTabData(tab_data);
        setTabLoader(false);
    }, [nomineeDetail]);

    useEffect(() => {
        if (props.data.length !== 0) {
            const updated_data = props.data.map(({
                relationship_id,
                relationship,
                guardian_relationship_id,
                guardian_relationship,
                date_of_birth,
                allocation,
                guardian_name,
                guardian_pan,
                ...rest
            },
                index) => {
                const formatted_date = format(parse(date_of_birth, 'dd-MM-yyyy', new Date()), 'dd/MM/yyyy');
                return {
                    ...rest,
                    allocation: parseFloat(allocation),
                    ageCheck: _is18YearsOld(formatted_date),
                    date_of_birth: formatted_date,
                    guardian_name: guardian_name === "-" ? null : guardian_name,
                    guardian_pan: guardian_pan === "-" ? null : guardian_pan,
                    guardian_relationship: guardian_relationship_id === "-" ? null : {
                        value: guardian_relationship_id,
                        label: guardian_relationship
                    },
                    relation: {
                        value: relationship_id,
                        label: relationship
                    },
                    proof: rest.nominee_proof_type_id !== null ? {
                        value: rest.nominee_proof_type_id,
                        label: rest.nominee_proof_type
                    } : null,
                    proof_number: rest.nominee_proof_id !== null ? rest.nominee_proof_id : "",
                    nominee_mobile: rest.nominee_mobile !== null ? rest.nominee_mobile : "",
                    nominee_email: rest.nominee_email !== null ? rest.nominee_email : "",
                    nominee_address_line1: rest.nominee_address_line1 !== null ? rest.nominee_address_line1 : "",
                    nominee_address_line2: rest.nominee_address_line2 !== null ? rest.nominee_address_line2 : "",
                    nominee_address_line3: rest.nominee_address_line3 !== null ? rest.nominee_address_line3 : "",
                    country: rest.nominee_country_id !== null ? {
                        value: rest.nominee_country_id,
                        label: rest.nominee_country
                    } : { label: "India", value: 101 },

                    state: rest.nominee_state_id !== null && rest.nominee_country_id === 101 ? {
                        value: rest.nominee_state_id,
                        label: rest.nominee_state
                    } : rest.nominee_state_name !== null ? rest.nominee_state_name : null,
                    city: rest.nominee_city_id !== null && rest.nominee_country_id === 101 ? {
                        value: rest.nominee_city_id,
                        label: rest.nominee_city
                    } : rest.nominee_city_name !== null ? rest.nominee_city_name : null,
                    pincode: rest.nominee_pincode !== null ? rest.nominee_pincode : "",
                    email_declaration: rest.nominee_email_declaration_id !== null ? {
                        value: rest.nominee_email_declaration_id,
                        label: rest.nominee_email_declaration
                    } : null,
                    mobile_declaration: rest.nominee_mobile_declaration !== null ? {
                        value: rest.nominee_mobile_declaration_id,
                        label: rest.nominee_mobile_declaration
                    } : null,
                    states: rest.nominee_country_id !== null ? _getStates({
                        value: rest.nominee_country_id,
                        label: rest.nominee_country
                    }, index) : _getStates({ label: "India", value: 101 }, index),
                    cities: rest.nominee_state_id !== null ? _getCities({
                        value: rest.nominee_state_id,
                        label: rest.nominee_state
                    }, index) : [],
                    soa_declaration: SOA_DECLARATIONS[0].id
                }
            });

            for (let i = updated_data.length; i > 1; i--) {
                nomineeErrorRef.current.push(NOMINEE_ERROR);
            }
            setNomineeDetail(updated_data);
        } else {
            setNomineeDetail([NOMINEE]);
        }
        _getGeneralData();
    }, [props.data]);

    const _handleClose = () => {
        props.close();
    }

    const _handleRadioInput = (e) => {
        setSelectedOptOptions(e);
        if (e === 2) {
            for (const key of Object.keys(NOMINEE_ERROR)) {
                nomineeErrorRef.current[tabIndex - 1][key] = '';
            }
            toast.dismiss();
            toast.success("You have decided not to add any nominee to your investment account.", {
                type: "success",
            });

        }
    }

    // handler for tab change
    const _handleTabSelect = (index) => {
        setTabIndex(index);
    }

    const _is18YearsOld = (dobString) => {
        // Parse the date of birth string into a Date object
        const dob = parse(dobString, 'dd/MM/yyyy', new Date());

        // Calculate the age difference
        const age_difference = differenceInYears(new Date(), dob);

        return age_difference >= 18;
    }

    const _handleInput = (type, value, key, is_text = false) => {
        let nominee = [...nomineeDetail];

        switch (type) {
            case 1:
                // Create a new copy of the array
                nominee[key] = { ...nominee[key], name: value }; // Update the specific element
                nomineeErrorRef.current[key].name = "";
                setNomineeDetail(nominee);
                break;
            case 2:
                nominee[key] = { ...nominee[key], date_of_birth: value, ageCheck: _is18YearsOld(value) }; // Update the specific element
                if (_is18YearsOld(value) === true) {
                    nominee[key] = { ...nominee[key], guardian_name: "" }
                    nominee[key] = { ...nominee[key], guardian_relationship: null }
                    nominee[key] = { ...nominee[key], guardian_pan: "" }
                    nomineeErrorRef.current[key].guardian_name = "";
                    nomineeErrorRef.current[key].guardian_relationship = "";
                    nomineeErrorRef.current[key].guardian_pan = "";
                }
                nomineeErrorRef.current[key].date_of_birth = "";
                setNomineeDetail(nominee);
                break;
            case 3:
                nominee[key] = { ...nominee[key], relation: value }; // Update the specific element
                nomineeErrorRef.current[key].relation = "";
                setNomineeDetail(nominee);
                break;
            case 4:
                nominee[key] = { ...nominee[key], allocation: value }; // Update the specific element
                nomineeErrorRef.current[key].allocation = "";
                setNomineeDetail(nominee);
                break;
            case 5:
                nominee[key] = { ...nominee[key], guardian_name: value }; // Update the specific element
                nomineeErrorRef.current[key].guardian_name = "";
                setNomineeDetail(nominee);
                break;
            case 6:
                nominee[key] = { ...nominee[key], guardian_relationship: value }; // Update the specific element
                nomineeErrorRef.current[key].guardian_relationship = "";
                setNomineeDetail(nominee);
                break;
            case 7:
                nominee[key] = { ...nominee[key], guardian_pan: value }; // Update the specific element
                nomineeErrorRef.current[key].guardian_pan = "";
                setNomineeDetail(nominee);
                break;
            case 8:
                nominee[key] = { ...nominee[key], proof: value, proof_number: "" }; // Update the specific element
                nomineeErrorRef.current[key].proof = "";
                setNomineeDetail(nominee);
                break;
            case 9:
                nominee[key] = { ...nominee[key], proof_number: value }; // Update the specific element
                nomineeErrorRef.current[key].proof_number = "";
                setNomineeDetail(nominee);
                break;
            case 10:
                nominee[key] = { ...nominee[key], nominee_email: value }; // Update the specific element
                nomineeErrorRef.current[key].nominee_email = "";
                setNomineeDetail(nominee);
                break;
            case 11:
                nominee[key] = { ...nominee[key], nominee_mobile: value }; // Update the specific element
                nomineeErrorRef.current[key].nominee_mobile = "";
                setNomineeDetail(nominee);
                break;
            case 12:
                nominee[key] = { ...nominee[key], nominee_address_line1: value }; // Update the specific element
                nomineeErrorRef.current[key].nominee_address_line1 = "";
                setNomineeDetail(nominee);
                break;
            case 13:
                nominee[key] = { ...nominee[key], nominee_address_line2: value }; // Update the specific element
                nomineeErrorRef.current[key].nominee_address_line2 = "";
                setNomineeDetail(nominee);
                break;
            case 14:
                nominee[key] = { ...nominee[key], nominee_address_line3: value }; // Update the specific element
                nomineeErrorRef.current[key].nominee_address_line3 = "";
                setNomineeDetail(nominee);
                break;
            case 15:
                // nominee[key] = { ...nominee[key], state: value }; // Update the specific element
                // nomineeErrorRef.current[key].state = "";
                _getStates(value, key)
                // setNomineeDetail(nominee);
                break;
            case 16:
                // nominee[key] = { ...nominee[key], city: value }; // Update the specific element
                // nomineeErrorRef.current[key].city = "";
                if (is_text === true) {
                    nominee[key] = { ...nominee[key], state: value }; // Update the specific element
                    nomineeErrorRef.current[key].state = "";
                    setNomineeDetail(nominee);
                    break;
                } else {
                    _getCities(value, key)
                }
                // setNomineeDetail(nominee);
                break;
            case 17:
                nominee[key] = { ...nominee[key], city: value }; // Update the specific element
                nomineeErrorRef.current[key].city = "";
                setNomineeDetail(nominee);
                break;
            case 18:
                nominee[key] = { ...nominee[key], pincode: value }; // Update the specific element
                nomineeErrorRef.current[key].pincode = "";
                setNomineeDetail(nominee);
                break;
            case 19:
                nominee[key] = { ...nominee[key], email_declaration: value }; // Update the specific element
                nomineeErrorRef.current[key].email_declaration = "";
                setNomineeDetail(nominee);
                break;
            case 20:
                nominee[key] = { ...nominee[key], mobile_declaration: value }; // Update the specific element
                nomineeErrorRef.current[key].mobile_declaration = "";
                setNomineeDetail(nominee);
                break;
            case 21:
                nominee[key] = { ...nominee[key], soa_declaration: value }; // Update the specific element
                nomineeErrorRef.current[key].soa_declaration = "";
                setNomineeDetail(nominee);
                break;
        }
    }

    const _validateMinor = (type) => {
        let valid = true;
        let nominee = [...nomineeDetail];
        for (let index = 0; index < nomineeErrorRef.current.length; index++) {
            const element = nomineeErrorRef.current[index];
            const is_empty = Object.values(element).every(value => value === "");
            if (is_empty === true) {
                setTabIndex(index + 1);
            }
        }


        nominee.map((item, key) => {

            // Mandatory per SEBI: nominee name + relationship. All other details are optional.
            if (item.name.length === 0) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], name: "Name is required" };
                valid = false;
            }

            if (item.relation === null) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], relation: "Relation is required" };
                valid = false;
            }


        });
        if (valid === true) {
            if (type === 1) {
                // Allocation percentage is optional (SEBI). Only enforce the 100% rule when every nominee has one.
                const allocations_provided = nominee.every(n => n.allocation !== "" && n.allocation !== null && n.allocation !== undefined);
                const total_allocation = nominee.reduce((acc, curr) => acc + (parseInt(curr.allocation) || 0), 0);
                if (allocations_provided && total_allocation !== 100) {
                    valid = false;
                    toast.dismiss();
                    toast.error("Sum of allocations must be 100%", {
                        type: "error"
                    });
                } else {
                    setIsMinor(true)
                }
            } else {
                _handleMore();
            }
        }
        setForceUpdate(!forceUpdate);
    }

    const _validateForm = (type) => {
        let valid = true;
        let nominee = [...nomineeDetail];
        for (let index = 0; index < nomineeErrorRef.current.length; index++) {
            const element = nomineeErrorRef.current[index];
            const is_empty = Object.values(element).every(value => value === "");
            if (is_empty === true) {
                setTabIndex(index + 1);
            }
        }


        nominee.map((item, key) => {


            if (item.name.length === 0) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], name: "Name is required" };
                valid = false;
            }

            // Date of birth is optional for adults; for a minor it is inherently required (minor status is derived from the DOB).

            if (item.relation === null) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], relation: "Relation is required" };
                valid = false;
            }


            // Allocation percentage is optional (validated for total only when provided).

            // All additional nominee details are optional per SEBI. Validate format only when a value is provided.
            // Guardian details (for minors) — validate PAN format only when provided.
            if (item.guardian_pan !== null && item.guardian_pan !== "" && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(item.guardian_pan?.toLocaleUpperCase())) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], guardian_pan: "Invalid Guardian pan " };
                valid = false;
            }
            // Identity proof — validate PAN format only when provided.
            if (item.proof !== null && item.proof.label == "Pan" && item.proof_number !== "" && (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(item.proof_number.toLocaleUpperCase()))) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], proof_number: "Invalid PAN" };
                valid = false;
            }
            // Email — validate format only when provided.
            if (item.nominee_email !== "" && (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(item.nominee_email))) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], nominee_email: "Invalid email" };
                valid = false;
            }
            // Mobile — validate length only when provided.
            if (item.nominee_mobile !== "" && item.nominee_mobile.length !== 10) {
                nomineeErrorRef.current[key] = { ...nomineeErrorRef.current[key], nominee_mobile: "Enter a valid 10-digit mobile number" };
                valid = false;
            }

            // (Adult-nominee required fields removed — all additional details are optional per SEBI; format is validated above when provided.)
            if (valid == false) {
                return false;
            }
        });
        if (valid === true) {
            if (type === 1) {
                // Allocation percentage is optional (SEBI). Only enforce the 100% rule when every nominee has one.
                const allocations_provided = nominee.every(n => n.allocation !== "" && n.allocation !== null && n.allocation !== undefined);
                const total_allocation = nominee.reduce((acc, curr) => acc + (parseInt(curr.allocation) || 0), 0);
                if (allocations_provided && total_allocation !== 100) {
                    valid = false;
                    toast.dismiss();
                    toast.error("Sum of allocations must be 100%", {
                        type: "error"
                    });
                } else {
                    let proceed_nominees = _processNomineeData();
                    _validateNominee(proceed_nominees);
                }
            } else {
                _handleMore();
            }
        }
        setForceUpdate(!forceUpdate);
    }

    const _handleMore = () => {
        let nominee = [...nomineeDetail]
        nominee.push(NOMINEE)
        nomineeErrorRef.current.push(NOMINEE_ERROR);
        setNomineeDetail(nominee);
        setTabIndex(prevState => prevState + 1);
        _getStates({ label: "India", value: 101 }, nominee.length - 1)
    }

    // API - get countries
    const _getCountries = () => {
        const url = "general/countries";

        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                const countries = response.data.map((item) => {
                    return ({
                        label: item.name,
                        value: item.country_id
                    })
                })
                if (props.data.length === 0) {
                    _getStates({ label: "India", value: 101 }, 0)
                }
                setCountryOptions(countries);
            }
        })
    }

    // API - get countries
    const _getStates = (e, index) => {
        const url = "general/states";

        setNomineeDetail((prev) => {
            const nominee = [...prev];

            // Prevent potential crash on undefined index
            // if (!nominee[index]) return prev;

            nominee[index] = {
                ...nominee[index],
                country: e,
                state: null,
                city: null,
                states: [], // clear previous states immediately
            };

            APIService(true, url, { country_id: Number(e.value) }).then((response) => {
                if (response.status_code === 200) {
                    const states = response.data.map((item) => ({
                        label: item.name,
                        value: item.state_id
                    }));

                    // Update states again after async call
                    setNomineeDetail((prevNominee) => {
                        const updated = [...prevNominee];
                        if (!updated[index]) return prevNominee;

                        updated[index] = {
                            ...updated[index],
                            states: states
                        };
                        nomineeErrorRef.current[index].country = "";
                        return updated;
                    });
                } else {
                    setNomineeDetail((prevNominee) => {
                        const updated = [...prevNominee];
                        // if (!updated[index]) return prevNominee;

                        updated[index] = {
                            ...updated[index],
                            states: []
                        };
                        console.log(updated)
                        return updated;
                    });
                }
            });

            return nominee;
        });
    };


    // API - get cities
    const _getCities = (e, index) => {

        const url = "general/cities";

        setNomineeDetail((prev) => {
            const nominee = [...prev];

            // Prevent potential crash on undefined index
            // if (!nominee[index]) return prev;

            nominee[index] = {
                ...nominee[index],
                state: e,
                city: null,
                cities: [], // clear previous states immediately
            };

            APIService(true, url, { state_id: Number(e.value) }).then((response) => {
                if (response.status_code === 200) {
                    const cities = response.data.map((item) => ({
                        label: item.name,
                        value: item.city_id
                    }));

                    // Update states again after async call
                    setNomineeDetail((prevNominee) => {
                        const updated = [...prevNominee];
                        if (!updated[index]) return prevNominee;

                        updated[index] = {
                            ...updated[index],
                            cities: cities
                        };
                        nomineeErrorRef.current[index].state = "";
                        return updated;
                    });
                }
            });

            return nominee;
        });
    }

    const _removeNominee = () => {
        let nominee_detail = [...nomineeDetail];
        nominee_detail.splice(tabIndex - 1, 1);
        nomineeErrorRef.current.splice(tabIndex - 1, 1);
        if (nominee_detail.length === 0) {
            nominee_detail = [NOMINEE];
        }
        setTabIndex(1);
        setTabLoader(true);
        setDeleteHover(false);
        setNomineeDetail([...nominee_detail]);
    }

    const _handleGoback = () => {
        setPinString("");
        setPinError("");
        setProceed(false);
    }

    const _handlePin = (value) => {
        setPinString(value);
        setPinError("");
    }

    function _navigateToProfile() {
        navigator('/profile', { state: { tab: location.state.tabIndex } })
    }

    // validate pin
    const _validatePin = async () => {
        let valid = true;
        if (pinString.length === 0) {
            setPinError("Enter OTP");
            valid = false;
        }
        if (valid === true) {
            let process_nominees = [];
            if (selectedOptOptions === 1) {
                process_nominees = await _processNomineeData();
            }
            _addNominee(process_nominees);
        }
    }

    function _processNomineeData() {
        const updated_data = nomineeDetail.map(({ name, relation, guardian_name, guardian_relationship, date_of_birth, ageCheck, date_of_birth_str, guardian_pan, proof, proof_number, nominee_email, nominee_mobile, nominee_address_line1, nominee_address_line2, nominee_address_line3, country, state, city, pincode, email_declaration, mobile_declaration, soa_declaration, ...rest }) => {
            // Parse the date_of_birth and format it as 'dd-MM-yyyy'

            // DOB is optional now — only format it when provided.
            const formatted_date = date_of_birth ? format(parse(date_of_birth, 'dd/MM/yyyy', new Date()), 'dd-MM-yyyy') : null;

            return {
                // ...rest,
                nominee_id: rest.nominee_id,
                name: name,
                allocation: rest.allocation,
                relation_id: relation?.value,
                guardian_name: ageCheck === true ? null : guardian_name,
                guardian_pan: ageCheck === true ? null : guardian_pan,
                guardian_relationship_id: guardian_relationship !== null ? guardian_relationship.value : null,
                date_of_birth: formatted_date,
                nominee_proof_type_id: proof !== null ? proof.value : null,
                nominee_proof_id: proof_number,
                nominee_email: nominee_email,
                nominee_mobile: nominee_mobile,
                nominee_address_line1: nominee_address_line1,
                nominee_address_line2: nominee_address_line2,
                nominee_address_line3: nominee_address_line3,
                nominee_state_id: state !== null ? state.value : null,
                nominee_city_id: city !== null ? city.value : null,
                nominee_state_name: state !== null && country?.value !== 101 ? state : null,
                nominee_city_name: city !== null && country?.value !== 101 ? city : null,
                nominee_country_id: country !== null ? country.value : null,
                nominee_pincode: pincode,
                nominee_mobile_declaration_id: mobile_declaration !== null ? mobile_declaration.value : null,
                nominee_email_declaration_id: email_declaration !== null ? email_declaration.value : null,
                soa_declaration: soa_declaration === 1 ? true : false
            };
        });
        return updated_data;
    }


    // API - Get general data (to get relation and guardian options)
    function _getGeneralData() {
        let relation_data = GENERAL_DATA.nominee_relation.map((item) => {
            return ({ value: item.id, label: item.name })
        });
        let guardian_data = GENERAL_DATA.nominee_guardian_relation.map((item) => {
            return ({ value: item.id, label: item.name })
        });
        setGuardianOptions(guardian_data);
        setRelationOptions(relation_data);
        setLoader(false);
    }

    // API - add nominee
    function _addNominee(nomineeData) {
        setApiLoader(true);
        let url = 'profile/nominee/upsert';
        if (props.nft === true) {
            url = 'mf-services/nft/verify-nominee-otp';
        }

        let payload = JSON.stringify({
            is_nominee_available: selectedOptOptions === 1 ? true : false,
            otp: pinString,
            nominee: selectedOptOptions === 1 ? nomineeData : [],
        });

        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (props.nft === true) {
                    setShowSuccess(true);
                } else {
                    document.getElementById("close-modal").dispatchEvent(new Event("click"));
                    if (selectedOptOptions === 1) {
                        toast.dismiss();
                        toast.success("Nominee details updated sucessfully", {
                            type: "success",
                        });
                    } else {
                        toast.dismiss();
                        toast.success("Nominee opted-out successfully", {
                            type: "success",
                        });
                    }
                    _getProfile()

                    props.update();
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error"
                });
            }
            setApiLoader(false);
        });
    }

    // API - validate nominee
    function _validateNominee(nomineeData, resend = false) {
        if (resend === false) {
            setApiLoader(true);
        }

        let url = 'profile/nominee/confirm-update';
        let payload = JSON.stringify({
            is_nominee_available: selectedOptOptions === 1 ? true : false,
            nominee: selectedOptOptions === 1 ? nomineeData : [],
        });
        if (props.nft === true) {
            url = 'mf-services/nft/change-nominee';
            payload = JSON.stringify({
                folio_numbers: [props.folio],
                is_nominee_available: selectedOptOptions === 1 ? true : false,
                nominee: selectedOptOptions === 1 ? nomineeData : [],
            });
        }


        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("OTP sent successfully", {
                    type: "success",
                });

                setProceed(true);

                // _updateCountdown();


            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error"
                });
            }
            setApiLoader(false);
        });
    }

    const _handleResendOTP = () => {
        if (props.nft === true) {
            let proceed_nominees = _processNomineeData();
            _validateNominee(proceed_nominees, true);
            setSeconds(30);
            setPinString("");
            return;
        }
        _consent2FA("NOMINEE")
            .then((response) => {
                toast.dismiss();
                toast.success("OTP sent successfully", {
                    type: "success",
                });
                setSeconds(30);
                setPinString("");
            }).catch((response) => {
                toast.dismiss();
                toast.error(response, {
                    type: "error"
                });
            });
    }

    return (
        <div className={`modal fade ${style.e_nominee_modal}`}
            id="manage-nominee"
            tabIndex="-1"
            aria-labelledby="manage-nominee"
            aria-hidden="true"
            data-bs-backdrop="true" >
            <div className={`modal-dialog position-relative modal-dialog-centered ${style.e_dialog}`}>
                <div className={`modal-content border-radius-24px border-all border-anti-flash-white padding-32px-all ${style.e_content} `}>
                    <span id="close-modal" data-bs-dismiss="modal" className='d-none'></span>
                    {
                        proceed === false && showSuccess === false ?
                            <Fragment>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <p className="color-jett-black e-poppins-semi-bold e-font-18 mb-0">Nominee details</p>
                                    <Icon icon="close"
                                        size={24}
                                        data-bs-dismiss="modal"
                                        className="cursor-pointer e-modal-close-btn"
                                        onClick={_handleClose} />
                                </div>
                                <div className={style.e_nominee_scroll}>

                                    <p className="color-outer-space e-poppins-regular e-font-14 mb-3">Add nominee to your investment account.</p>
                                    <RadioGroup data={OptOptions}
                                        className={`gap-16px `}
                                        radioItem={"w-unset"}
                                        spanClass="e-poppins-regular"
                                        iconClassName="me-2"
                                        size={20}
                                        type={1}
                                        id={selectedOptOptions}
                                        radioHandler={_handleRadioInput} />
                                    {
                                        selectedOptOptions === 1 ?
                                            <div className="mt-3 d-flex justify-content-between align-items-center margin-28px-bottom">
                                                <CustomTab data={tabData}
                                                    selectedTab={tabIndex}
                                                    wrapperClass={'gap-16px'}
                                                    className="px-0 padding-10px-tb e-order-tab"
                                                    onSelectTab={_handleTabSelect} />

                                                <div className="d-flex align-items-center gap-10px">
                                                    {
                                                        nomineeDetail.length > 1 && (
                                                            <div className="position-relative">
                                                                <Icon icon={deleteHover === true ? "delete-red" : "delete"}
                                                                    size={24}
                                                                    className={`${nomineeDetail.length === 1 && "no-events"} cursor-pointer`}
                                                                    onMouseEnter={() => setDeleteHover(true)}
                                                                    onMouseLeave={() => setDeleteHover(false)}
                                                                    onClick={_removeNominee} />
                                                                {
                                                                    deleteHover === true &&
                                                                    <p className={`border-radius-4px color-outer-space e-poppins-regular e-font-12 line-height-12 mb-0 p-2 bg-white position-absolute white-space-nowrap z-index-1 ${style.e_delete_icon}`}>
                                                                        Delete nominee
                                                                    </p>
                                                                }
                                                            </div>
                                                        )
                                                    }

                                                    {
                                                        nomineeDetail.length < 3 &&
                                                        <OutlineButton label="Add new"
                                                            className="px-3 e-font-sm-16-14 padding-12px-tb-sm-8"
                                                            handleClick={() => _validateForm(2)} />
                                                    }
                                                </div>
                                            </div>
                                            :
                                            <>
                                                <h5 className='e-poppins-semi-bold e-font-16  color-outer-space d-block mt-3 mt-5'>
                                                    You have selected not to add nominee to your investments
                                                </h5>

                                                <h5 className='e-poppins-semi-regular e-font-14 line-height-26px color-outer-space d-block'>
                                                    You can always update nominee by choosing Opt-in anytime
                                                </h5>
                                            </>
                                    }

                                    {
                                        loader === true || tabLoader === true ?
                                            <CustomLoader />
                                            :
                                            <Fragment>
                                                {
                                                    selectedOptOptions === 1 && (
                                                        <div className="row mt-4"
                                                        // style={{ overflowY: nomineeDetail[tabIndex - 1].ageCheck === false ? "scroll" : "visible" }}
                                                        >
                                                            {isMinor === false && <Fragment>
                                                                <div className="col-12">
                                                                    <CustomTextInput label="Name"
                                                                        postfix="*"
                                                                        value={nomineeDetail[tabIndex - 1].name}
                                                                        className="margin-32px-bottom"
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['name']}
                                                                        handleChange={(data) => _handleInput(1, data, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-12">
                                                                    <CustomSelectBox value={nomineeDetail[tabIndex - 1].relation}
                                                                        label="Relation"
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['relation']}
                                                                        postfix="*"
                                                                        className="margin-32px-bottom "
                                                                        options={relationOptions}
                                                                        onSelectChange={(value) => _handleInput(3, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-12 col-md-12 col-12">
                                                                    <DatePicker label="Date of Birth"
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['date_of_birth']}
                                                                        value={nomineeDetail[tabIndex - 1].date_of_birth}
                                                                        isAdult={true}
                                                                        maxDate={maxDate}
                                                                        dob={(value) => _handleInput(2, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-12 col-md-12 col-12 mt-4 pt-2">
                                                                    <CustomTextInput label="Allocation percentage"
                                                                        type="number"
                                                                        maxLength={3}
                                                                        value={nomineeDetail[tabIndex - 1].allocation}
                                                                        className="margin-32px-bottom "
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['allocation']}
                                                                        handleChange={(value) => _handleInput(4, value, tabIndex - 1)} />
                                                                </div>
                                                            </Fragment>}
                                                            {
                                                                isMinor === true &&
                                                                <Fragment>
                                                                    {/* Guardian fields apply only to a MINOR nominee (per-nominee ageCheck), not the whole form */}
                                                                    {nomineeDetail[tabIndex - 1].ageCheck === false && <Fragment>
                                                                    <div className="col-12">
                                                                        <CustomTextInput label="Guardian name"
                                                                            value={nomineeDetail[tabIndex - 1].guardian_name}
                                                                            className="margin-32px-bottom"
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['guardian_name']}
                                                                            handleChange={(data) => _handleInput(5, data, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-12">
                                                                        <CustomSelectBox value={nomineeDetail[tabIndex - 1].guardian_relationship}
                                                                            label="Guardian relation"
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['guardian_relationship']}
                                                                            className="margin-32px-bottom "
                                                                            options={guardianOptions}
                                                                            onSelectChange={(value) => _handleInput(6, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-12">
                                                                        <CustomTextInput label="Guardian PAN number"
                                                                            pan={true}
                                                                            inputClass="text-uppercase"
                                                                            value={nomineeDetail[tabIndex - 1].guardian_pan}
                                                                            className="margin-32px-bottom"
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['guardian_pan']}
                                                                            handleChange={(data) => _handleInput(7, data, tabIndex - 1)} />
                                                                    </div>
                                                                    </Fragment>}
                                                                    <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomSelectBox value={nomineeDetail[tabIndex - 1].proof}
                                                                            label="Select Identity document"
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['proof']}
                                                                            className="margin-32px-bottom "
                                                                            options={identityProofOptions}
                                                                            onSelectChange={(value) => _handleInput(8, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomTextInput label={`${nomineeDetail[tabIndex - 1].proof !== null ? nomineeDetail[tabIndex - 1].proof.label == "Aadhaar" ? "Las 4 digits of Aadhaar" : nomineeDetail[tabIndex - 1].proof.label : "Idenitity"} number`}
                                                                            type={nomineeDetail[tabIndex - 1].proof !== null && nomineeDetail[tabIndex - 1].proof.label == "Aadhaar" ? "number" : "text"}
                                                                            maxLength={nomineeDetail[tabIndex - 1].proof !== null ? nomineeDetail[tabIndex - 1].proof.label == "Passport" ? 9 : nomineeDetail[tabIndex - 1].proof.label == "Aadhaar" ? 4 : nomineeDetail[tabIndex - 1].proof.label == "Driving license" ? 20 : 10 : undefined}
                                                                            value={nomineeDetail[tabIndex - 1].proof_number}
                                                                            className={`margin-32px-bottom`}
                                                                            inputClass={nomineeDetail[tabIndex - 1].proof !== null && nomineeDetail[tabIndex - 1].proof.label == "Pan" ? "text-uppercase" : ""}
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['proof_number']}
                                                                            handleChange={(value) => _handleInput(9, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomTextInput label="Phone number"
                                                                            type="number"
                                                                            maxLength={10}
                                                                            value={nomineeDetail[tabIndex - 1].nominee_mobile}
                                                                            className="margin-32px-bottom "
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['nominee_mobile']}
                                                                            handleChange={(value) => _handleInput(11, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomTextInput label="Email"
                                                                            type="email"
                                                                            value={nomineeDetail[tabIndex - 1].nominee_email}
                                                                            className="margin-32px-bottom "
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['nominee_email']}
                                                                            handleChange={(value) => _handleInput(10, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-12 pb-3">
                                                                        <div className="d-flex gap-1 bg-beige border-radius-8px p-3">
                                                                            <span>
                                                                                <Icon icon="info"
                                                                                    size={20} />
                                                                            </span>
                                                                            <p className="e-font-14 e-poppins-regular line-height-24px color-jett-black mb-0 ">
                                                                                Each Address Line can be up to 40 characters long, with hyphen (-) and oblique (/) allowed as special characters.
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomTextInput label="Address Line 1"
                                                                            type="text"
                                                                            maxLength={40}
                                                                            is_address
                                                                            value={nomineeDetail[tabIndex - 1].nominee_address_line1}
                                                                            className="margin-32px-bottom "
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['nominee_address_line1']}
                                                                            handleChange={(value) => _handleInput(12, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomTextInput label="Address Line 2"
                                                                            type="text"
                                                                            maxLength={40}
                                                                            is_address
                                                                            value={nomineeDetail[tabIndex - 1].nominee_address_line2}
                                                                            className="margin-32px-bottom "
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['nominee_address_line2']}
                                                                            handleChange={(value) => _handleInput(13, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomTextInput label="Address Line 3"
                                                                            type="text"
                                                                            maxLength={40}
                                                                            is_address
                                                                            value={nomineeDetail[tabIndex - 1].nominee_address_line3}
                                                                            className="margin-32px-bottom "
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['nominee_address_line3']}
                                                                            handleChange={(value) => _handleInput(14, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomSelectBox value={nomineeDetail[tabIndex - 1].country}
                                                                            label="Country"
                                                                            isSearchable={true}
                                                                            className="margin-32px-bottom"
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['country']}
                                                                            options={countryOption}
                                                                            onSelectChange={(e) => {
                                                                                _handleInput(15, e, tabIndex - 1);
                                                                            }} />
                                                                    </div>
                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        {
                                                                            nomineeDetail[tabIndex - 1].states.length > 0 ?
                                                                                <CustomSelectBox value={nomineeDetail[tabIndex - 1].state}
                                                                                    label="State"
                                                                                    isSearchable={true}
                                                                                    className="margin-32px-bottom"
                                                                                    isDisabled={nomineeDetail[tabIndex - 1].country === null ? true : false}
                                                                                    error={nomineeErrorRef.current[tabIndex - 1]['state']}
                                                                                    options={nomineeDetail[tabIndex - 1].states || []}
                                                                                    onSelectChange={(e) => {
                                                                                        _handleInput(16, e, tabIndex - 1, false);

                                                                                    }} />
                                                                                :
                                                                                <CustomTextInput label="State"
                                                                                    type="text"
                                                                                    value={nomineeDetail[tabIndex - 1].state}
                                                                                    className="margin-32px-bottom "
                                                                                    error={nomineeErrorRef.current[tabIndex - 1]['state']}
                                                                                    handleChange={(value) => _handleInput(16, value, tabIndex - 1, true)} />
                                                                        }
                                                                    </div>

                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        {
                                                                            nomineeDetail[tabIndex - 1].states.length > 0 ?
                                                                                <CustomSelectBox value={nomineeDetail[tabIndex - 1].city}
                                                                                    label="City"
                                                                                    isSearchable={true}
                                                                                    className="margin-32px-bottom"
                                                                                    error={nomineeErrorRef.current[tabIndex - 1]['city']}
                                                                                    isDisabled={nomineeDetail[tabIndex - 1].state === null ? true : false}
                                                                                    options={nomineeDetail[tabIndex - 1].cities || []}
                                                                                    onSelectChange={(e) => {
                                                                                        _handleInput(17, e, tabIndex - 1);
                                                                                    }} />
                                                                                :
                                                                                <CustomTextInput label="City"
                                                                                    type="text"
                                                                                    value={nomineeDetail[tabIndex - 1].city}
                                                                                    className="margin-32px-bottom "
                                                                                    error={nomineeErrorRef.current[tabIndex - 1]['city']}
                                                                                    handleChange={(value) => _handleInput(17, value, tabIndex - 1)} />
                                                                        }
                                                                    </div>
                                                                    <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomTextInput label="Pincode"
                                                                            type="number"
                                                                            maxLength={6}
                                                                            value={nomineeDetail[tabIndex - 1].pincode}
                                                                            className="margin-32px-bottom "
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['pincode']}
                                                                            handleChange={(value) => _handleInput(18, value, tabIndex - 1)} />
                                                                    </div>
                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomSelectBox value={nomineeDetail[tabIndex - 1].email_declaration}
                                                                            label="Email declaration"
                                                                            isSearchable={true}
                                                                            className="margin-32px-bottom"
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['email_declaration']}
                                                                            options={declarationOptions}
                                                                            onSelectChange={(e) => {
                                                                                _handleInput(19, e, tabIndex - 1);
                                                                            }} />
                                                                    </div>
                                                                    <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <CustomSelectBox value={nomineeDetail[tabIndex - 1].mobile_declaration}
                                                                            label="Mobile declaration"
                                                                            isSearchable={true}
                                                                            className="margin-32px-bottom"
                                                                            error={nomineeErrorRef.current[tabIndex - 1]['mobile_declaration']}
                                                                            options={declarationOptions}
                                                                            onSelectChange={(e) => {
                                                                                _handleInput(20, e, tabIndex - 1);
                                                                            }} />
                                                                    </div>

                                                                    <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                        <p className="color-black e-poppins-medium e-font-14 mb-1">Nominee Visibility on account statement</p>
                                                                        <RadioGroup data={SOA_DECLARATIONS}
                                                                            className={`gap-16px `}
                                                                            radioItem={"w-unset"}
                                                                            spanClass="e-poppins-regular"
                                                                            iconClassName="me-2"
                                                                            size={20}
                                                                            type={1}
                                                                            id={nomineeDetail[tabIndex - 1].soa_declaration}
                                                                            radioHandler={(value) => _handleInput(21, value, tabIndex - 1)} />
                                                                    </div>
                                                                </Fragment>

                                                            }
                                                            {nomineeDetail[tabIndex - 1].ageCheck === true && isMinor === false && <Fragment>
                                                                <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomSelectBox value={nomineeDetail[tabIndex - 1].proof}
                                                                        label="Select Identity document"
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['proof']}
                                                                        className="margin-32px-bottom "
                                                                        options={identityProofOptions}
                                                                        onSelectChange={(value) => _handleInput(8, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomTextInput label={`${nomineeDetail[tabIndex - 1].proof !== null ? nomineeDetail[tabIndex - 1].proof.label == "Aadhaar" ? "Las 4 digits of Aadhaar" : nomineeDetail[tabIndex - 1].proof.label : "Idenitity"} number`}
                                                                        type={nomineeDetail[tabIndex - 1].proof !== null && nomineeDetail[tabIndex - 1].proof.label == "Aadhaar" ? "number" : "text"}
                                                                        maxLength={nomineeDetail[tabIndex - 1].proof !== null ? nomineeDetail[tabIndex - 1].proof.label == "Passport" ? 9 : nomineeDetail[tabIndex - 1].proof.label == "Aadhaar" ? 4 : nomineeDetail[tabIndex - 1].proof.label == "Driving license" ? 20 : 10 : undefined}
                                                                        value={nomineeDetail[tabIndex - 1].proof_number}
                                                                        className="margin-32px-bottom "
                                                                        inputClass={nomineeDetail[tabIndex - 1].proof !== null && nomineeDetail[tabIndex - 1].proof.label == "Pan" ? "text-uppercase" : ""} error={nomineeErrorRef.current[tabIndex - 1]['proof_number']}
                                                                        handleChange={(value) => _handleInput(9, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomTextInput label="Phone number"
                                                                        type="number"
                                                                        maxLength={10}
                                                                        value={nomineeDetail[tabIndex - 1].nominee_mobile}
                                                                        className="margin-32px-bottom "
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['nominee_mobile']}
                                                                        handleChange={(value) => _handleInput(11, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomTextInput label="Email"
                                                                        type="email"
                                                                        value={nomineeDetail[tabIndex - 1].nominee_email}
                                                                        className="margin-32px-bottom "
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['nominee_email']}
                                                                        handleChange={(value) => _handleInput(10, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-12 pb-3">
                                                                    <div className="d-flex gap-1 bg-beige border-radius-8px p-3">
                                                                        <span>
                                                                            <Icon icon="info"
                                                                                size={20} />
                                                                        </span>
                                                                        <p className="e-font-14 e-poppins-regular line-height-24px color-jett-black mb-0 ">
                                                                            Each Address Line can be up to 40 characters long, with hyphen (-) and oblique (/) allowed as special characters.
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomTextInput label="Address Line 1"
                                                                        maxLength={40}
                                                                        type="text"
                                                                        is_address
                                                                        value={nomineeDetail[tabIndex - 1].nominee_address_line1}
                                                                        className="margin-32px-bottom "
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['nominee_address_line1']}
                                                                        handleChange={(value) => _handleInput(12, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomTextInput label="Address Line 2"
                                                                        type="text"
                                                                        value={nomineeDetail[tabIndex - 1].nominee_address_line2}
                                                                        className="margin-32px-bottom "
                                                                        maxLength={40}
                                                                        is_address
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['nominee_address_line2']}
                                                                        handleChange={(value) => _handleInput(13, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomTextInput label="Address Line 3"
                                                                        type="text"
                                                                        value={nomineeDetail[tabIndex - 1].nominee_address_line3}
                                                                        className="margin-32px-bottom "
                                                                        maxLength={40}
                                                                        is_address
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['nominee_address_line3']}
                                                                        handleChange={(value) => _handleInput(14, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomSelectBox value={nomineeDetail[tabIndex - 1].country}
                                                                        label="Country"
                                                                        isSearchable={true}
                                                                        className="margin-32px-bottom"
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['country']}
                                                                        options={countryOption}
                                                                        onSelectChange={(e) => {
                                                                            _handleInput(15, e, tabIndex - 1);
                                                                        }} />
                                                                </div>
                                                                {
                                                                    nomineeDetail[tabIndex - 1]?.states?.length > 0 ?
                                                                        <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                            <CustomSelectBox value={nomineeDetail[tabIndex - 1].state}
                                                                                label="State"
                                                                                isSearchable={true}
                                                                                className="margin-32px-bottom"
                                                                                isDisabled={nomineeDetail[tabIndex - 1].country === null ? true : false}
                                                                                error={nomineeErrorRef.current[tabIndex - 1]['state']}
                                                                                options={nomineeDetail[tabIndex - 1].states || []}
                                                                                onSelectChange={(e) => {
                                                                                    _handleInput(16, e, tabIndex - 1, false);

                                                                                }} />
                                                                        </div>
                                                                        :
                                                                        <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                            <CustomTextInput label="State"
                                                                                type="text"
                                                                                value={nomineeDetail[tabIndex - 1].state}
                                                                                className="margin-32px-bottom "
                                                                                error={nomineeErrorRef.current[tabIndex - 1]['state']}
                                                                                handleChange={(value) => _handleInput(16, value, tabIndex - 1, true)} />
                                                                        </div>
                                                                }

                                                                {
                                                                    nomineeDetail[tabIndex - 1]?.states?.length > 0 ?
                                                                        <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                            <CustomSelectBox value={nomineeDetail[tabIndex - 1].city}
                                                                                label="City"
                                                                                isSearchable={true}
                                                                                className="margin-32px-bottom"
                                                                                error={nomineeErrorRef.current[tabIndex - 1]['city']}
                                                                                isDisabled={nomineeDetail[tabIndex - 1].state === null ? true : false}
                                                                                options={nomineeDetail[tabIndex - 1].cities || []}
                                                                                onSelectChange={(e) => {
                                                                                    _handleInput(17, e, tabIndex - 1);
                                                                                }} />
                                                                        </div>
                                                                        :
                                                                        <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                            <CustomTextInput label="City"
                                                                                type="text"
                                                                                value={nomineeDetail[tabIndex - 1].city}
                                                                                className="margin-32px-bottom "
                                                                                error={nomineeErrorRef.current[tabIndex - 1]['city']}
                                                                                handleChange={(value) => _handleInput(17, value, tabIndex - 1)} />
                                                                        </div>
                                                                }
                                                                <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomTextInput label="Pincode"
                                                                        type="number"
                                                                        maxLength={6}
                                                                        value={nomineeDetail[tabIndex - 1].pincode}
                                                                        className="margin-32px-bottom "
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['pincode']}
                                                                        handleChange={(value) => _handleInput(18, value, tabIndex - 1)} />
                                                                </div>
                                                                <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomSelectBox value={nomineeDetail[tabIndex - 1].email_declaration}
                                                                        label="Email declaration"
                                                                        isSearchable={true}
                                                                        className="margin-32px-bottom"
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['email_declaration']}
                                                                        options={declarationOptions}
                                                                        onSelectChange={(e) => {
                                                                            _handleInput(19, e, tabIndex - 1);
                                                                        }} />
                                                                </div>
                                                                <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <CustomSelectBox value={nomineeDetail[tabIndex - 1].mobile_declaration}
                                                                        label="Mobile declaration"
                                                                        isSearchable={true}
                                                                        className="margin-32px-bottom"
                                                                        error={nomineeErrorRef.current[tabIndex - 1]['mobile_declaration']}
                                                                        options={declarationOptions}
                                                                        onSelectChange={(e) => {
                                                                            _handleInput(20, e, tabIndex - 1);
                                                                        }} />
                                                                </div>

                                                                <div className="col-lg-12 col-md-12 col-12 mt-lg-0 mt-md-0 mt-4">
                                                                    <p className="color-black e-poppins-medium e-font-14 mb-1">Nominee Visibility on account statement</p>
                                                                    <RadioGroup data={SOA_DECLARATIONS}
                                                                        className={`gap-16px `}
                                                                        radioItem={"w-unset"}
                                                                        spanClass="e-poppins-regular"
                                                                        iconClassName="me-2"
                                                                        size={20}
                                                                        type={1}
                                                                        id={nomineeDetail[tabIndex - 1].soa_declaration}
                                                                        radioHandler={(value) => _handleInput(21, value, tabIndex - 1)} />
                                                                </div>
                                                            </Fragment>}

                                                        </div>
                                                    )
                                                }
                                            </Fragment>
                                    }
                                </div>

                                {nomineeDetail[tabIndex - 1]?.ageCheck === false && isMinor === false && <div className="col-12">
                                    <div className="d-flex gap-2 align-items-start border-radius-16px bg-beige w-fit-content p-3 ">
                                        <span>
                                            <Icon icon="info"
                                                size={16} />
                                        </span>
                                        <p className="color-outer-space e-poppins-regular e-font-14 line-height-24px mb-0">
                                            As the nominee is identified as a minor based on the date of birth provided, it is mandatory to add the details of a legal guardian.
                                        </p>
                                    </div>
                                </div>}

                                <div className={selectedOptOptions === 1 ? "mx-5 pt-4" : "mx-5 mt-4 pt-4"}>
                                    <GradientButton label={nomineeDetail[tabIndex - 1]?.ageCheck === false && isMinor === false ? "Add Guardian Details " : "Verify & Save"}
                                        className="e-font-16 padding-12px-tb w-100"
                                        loading={apiLoader}
                                        handleClick={() => {
                                            if (selectedOptOptions === 1) {
                                                nomineeDetail[tabIndex - 1]?.ageCheck === false && isMinor === false ?
                                                    _validateMinor(1)
                                                    :
                                                    _validateForm(1);
                                            } else {

                                                _validateNominee([]);
                                            }
                                        }} />
                                </div>
                            </Fragment>
                            : proceed === true && showSuccess === false ?
                                <Fragment>
                                    <Icon icon="close"
                                        size={24}
                                        id="close-modal"
                                        data-bs-dismiss="modal"
                                        className={`position-absolute cursor-pointer e-modal-close-btn ${style.e_proceed_close}`} />
                                    <Icon icon="arrow-right1"
                                        size={24}
                                        color={Colors.black}
                                        className={`position-absolute cursor-pointer ${style.e_back} ${apiLoader === true && "no-events"}`}
                                        onClick={_handleGoback} />

                                    <div className="text-center mt-4">
                                        <img src={require("../../Assets/Images/portfolio/redeem-confirm.png")}
                                            alt="Confirm payment"
                                            width={72}
                                            height={72} />
                                        <p className="color-eerie-black e-poppins-medium e-font-18 mb-2 mt-3">Enter OTP you have received in registered email and phone number</p>

                                        <p className="color-jett-black e-poppins-regular e-font-16 mb-3">Submit OTP to update nominee details.</p>

                                        <div className="d-flex justify-content-center w-100">
                                            <OTPInput pinString={pinString}
                                                id="invest"
                                                type={props.nft === true ? 2 : 1}
                                                autoFocus={true}
                                                pinError={pinError}
                                                className={props.nft === true && 'e_width_386'}
                                                disabled={apiLoader}
                                                handleChange={_handlePin} />
                                        </div>

                                    </div>
                                    <div className="margin-22px-top mx-5 px-5">
                                        <GradientButton label="Confirm OTP"
                                            className=" e-font-16 padding-12px-tb w-100"
                                            loading={apiLoader}
                                            handleClick={_validatePin} />
                                    </div>
                                    <div className="d-flex justify-content-center mt-4">
                                        {
                                            seconds === 0 ?
                                                <p className='color-primary-color e-poppins-regular e-font-14 line-height-14px cursor-pointer mb-0 e-forgot-pin position-relative w-max-content'
                                                    onClick={_handleResendOTP}
                                                >Resend OTP</p>
                                                :
                                                <p className='color-primary-color e-poppins-regular e-font-14 line-height-14px cursor-not-allowed mb-0 '>
                                                    Resend OTP in <span className="color-black">{seconds}s</span>
                                                </p>
                                        }

                                    </div>
                                </Fragment>
                                :
                                <Fragment>
                                    <Icon icon="close"
                                        size={24}
                                        data-bs-dismiss="modal"
                                        className={`position-absolute cursor-pointer e-modal-close-btn ${style.e_proceed_close}`} />
                                    <div className="text-center">
                                        <img src={require('../../Assets/Images/orders/order-success.svg').default}
                                            alt="Pause SIP"
                                            width={72}
                                            height={72} />
                                    </div>
                                    <div className="row">
                                        <div className="col-12 text-center">
                                            <h5 className="color-eerie-black e-poppins-medium e-font-18 mt-5">
                                                Data added successfully.
                                            </h5>
                                            <p className="color-outer-space e-poppins-regular e-font-14 mb-0  text-center">
                                                Your request is successfully submitted.
                                            </p>
                                        </div>
                                        <div className="mt-4 pt-3 d-sm-flex d-block justify-content-center gap-8px mb-3">

                                            <GradientButton label="Back to home"
                                                cancel="modal"
                                                className="e-font-16 padding-70px-lr padding-12px-tb w-mobile-100 mt-sm-0 mt-3"
                                                handleClick={_navigateToProfile} />
                                        </div>
                                    </div>
                                </Fragment>
                    }


                </div>
            </div>

        </div>
    )
}


ManageNomineeModal.defaultProps = {
    data: [NOMINEE],
    nft: false,
}
export default ManageNomineeModal